import { Activity, useRef, type ReactNode } from 'react';

import { Arriving, arriving, settled, type Arrival } from './arrival';

/**
 * The dissolve, and the same number as `.pane--in` in the stylesheet.
 *
 * Declared twice because the two readers are different: the stylesheet runs the
 * animation, and this side has to tell the screen inside the pane when that run
 * is over — see `arrival.tsx` for why anything inside it must not arrive again.
 */
const FADE_MS = 140;

/**
 * One of the three tabs, taking the place of the one before it.
 *
 * Dissolving and not travelling, and that is the whole decision. A horizontal
 * run would say the three screens lie in a strip, and a strip promises a swipe
 * this app does not answer - the tab is chosen with a thumb at the bottom of
 * the phone. A dissolve says «another screen» and promises nothing.
 *
 * 140ms, which is short enough that it is a joint rather than a transition. The
 * cross-fade cut on 2026-08-14 as decoration was 260 with a horizontal run in
 * it; what is left here is the part that answers the thumb.
 *
 * **The first pane does not run.** The choir's key is above this - switching
 * choirs rebuilds the whole screen - and without this every choir switch would
 * dissolve as if a tab had been tapped, which is exactly the thing «смена хора
 * это не переключение вкладки» rules out. So the animation belongs to a pane
 * that replaced another one, and never to a pane that is the first thing here.
 *
 * **A tab that has been opened stays mounted** — C-023 (`FND-1618`), the audit
 * of 2026-10-27. Until then the pane was keyed by the tab, so every tap on the
 * bar threw the screen away and the next tap on the same tab built it again:
 * a skeleton, the same requests, and the blocks landing one by one — the
 * invitation card pushing «Я» down by 516 pixels on every return. Now each tab
 * that has been shown keeps its pane, hidden in an `Activity` while another is
 * up: the DOM and the state stay, the effects are taken down (no poll, no
 * back-key subscription from a tab that is not on screen) and are run again on
 * the way back — the same requests a mount would have made, at the same moment,
 * so `screen_views` is counted as before (`.claude/rules/contracts.md`). The
 * person sees the tab as they left it, and the fresh answer replaces it in
 * place. A tab never shown is not mounted: it asks nothing until it is opened.
 */
export default function Fade({ at, panes }: { at: string; panes: Record<string, ReactNode> }) {
  /**
   * What is on screen, whether it arrived over something, and until when it is
   * still arriving. Held rather than derived from a previous render, so that
   * the second render of the same pane - React does two of them under
   * StrictMode - does not decide the pane is no longer fresh and take the
   * animation off half way through it.
   */
  const shown = useRef<{ at: string; fresh: boolean; arrival: Arrival }>({
    at,
    fresh: false,
    arrival: settled(),
  });
  if (shown.current.at !== at) {
    shown.current = { at, fresh: true, arrival: arriving(FADE_MS) };
  }
  /** Which tabs have been on screen, in the order they first were. */
  const opened = useRef<string[]>([]);
  if (!opened.current.includes(at)) opened.current = [...opened.current, at];
  /** What a hidden pane reads: settled, and one object per pane, so nothing in it re-renders. */
  const resting = useRef<Map<string, Arrival>>(new Map());
  const rest = (name: string): Arrival => {
    let one = resting.current.get(name);
    if (one === undefined) {
      one = settled();
      resting.current.set(name, one);
    }
    return one;
  };

  return (
    <>
      {opened.current.map((name) => (
        <Activity key={name} mode={name === at ? 'visible' : 'hidden'}>
          <div className={name === at && shown.current.fresh ? 'pane pane--in' : 'pane'}>
            {/* The arrival is the one of the pane on screen; a hidden pane has
                nothing arriving in it and reads a settled one. */}
            <Arriving arrival={name === at ? shown.current.arrival : rest(name)}>
              {panes[name]}
            </Arriving>
          </div>
        </Activity>
      ))}
    </>
  );
}
